<script setup>
import {defineProps, computed, watch, defineEmits, onMounted, ref, nextTick} from 'vue'
import Jumbo from '@/components/Docs/Jumbo.vue'
import DocSection from '@/components/Docs/Section.vue'
import CodeBlock from '@/components/Docs/CodeBlock.vue'
import JsonBlock from '@/components/Docs/JsonBlock.vue'


import StringParam from '@/components/Docs/Parameters/StringParam.vue'
import EndpointBlock from '@/components/Docs/EndpointBlock.vue'


import BasicAttribute from '@/components/Docs/Attributes/BasicAttribute.vue'
import { provideDocExpansion } from '@/composables/useDocExpansion'

defineProps({ anchor: String })

let request_ip = ref("89.36.76.126")

let get_query = computed(() => {
  return "/query/ip/" + request_ip.value
})

let dynamic_title = computed(() => {
  return "/query/ip/" + request_ip.value
})

// Master expand/collapse control
const { triggerExpandAll } = provideDocExpansion()
const allExpanded = ref(false)

const toggleAllExpansion = () => {
  allExpanded.value = !allExpanded.value
  triggerExpandAll(allExpanded.value)
}

</script>

<template>
  <div>
    <doc-section title="IP lookup" :anchor="anchor">
      <template v-slot:left>
        <div class="mb-5">
        This endpoint returns a collection of intelligence on a single IP address,
        grouped by enrichment. The set of enrichments returned depends on what your
        organization is granted.
        <br><br>
        By default a fast, curated set runs: <code>geo</code>,
        <code>anonymization</code>, <code>osint</code> and <code>signals</code>.
        To request specific enrichments, add one or more repeated
        <code>enrichments</code> query parameters (e.g.
        <code>?enrichments=geo&amp;enrichments=osint</code>). Requesting an
        enrichment your organization is not granted returns
        <code>403 Forbidden</code>.
        </div>

        <div class="mb-5">
          <h4 class="mb-4">Request parameters</h4>

          <StringParam class="mb-3" name="IP address" :required="true" description="The IP address you wish to look up (path parameter)" type="string" :value="request_ip" @value="(v) => request_ip = v" />
        </div>

        <div>
          <div class="d-flex justify-content-between align-items-center mb-4">
            <h4 class="mb-0">Response attributes</h4>
            <button @click="toggleAllExpansion" class="btn-expand-collapse">
              <font-awesome-icon :icon="['fas', allExpanded ? 'compress-alt' : 'expand-alt']" />
              {{ allExpanded ? 'Collapse All' : 'Expand All' }}
            </button>
          </div>

          <BasicAttribute name="status" type="string" description="The status of the request's response" />

          <BasicAttribute name="data" type="object" description="The requested information">

            <BasicAttribute name="ip" type="string" description="The target IP address" />

            <BasicAttribute name="enrichments" type="object" description="Per-enrichment results, keyed by enrichment slug. Only enrichments your organization is granted are present (each may be an empty object)." :depth="1">

              <BasicAttribute name="webscout_geo" type="object" description="Geolocation, network, AS, company and hostname intelligence" :depth="2">
                <BasicAttribute name="location" type="object" description="The location of the ip" :depth="3">
                  <BasicAttribute name="country_iso" type="string" description="Two letter country code" />
                  <BasicAttribute name="city" type="string" description="The full name of the city" />
                </BasicAttribute>
                <BasicAttribute name="network" type="object" description="" :depth="3">
                  <BasicAttribute name="type" type="string" description="The likely type of infrastructure used to operate the ip" />
                  <BasicAttribute name="service" type="string" description="The service the ip is a part of. For instance S3, cdn.." />
                  <BasicAttribute name="region" type="string" description="This will usually map to a cloud providers regional mappings" />
                  <BasicAttribute name="range" type="string" description="The closest allocation of addresses associated with the ip address" />
                  <BasicAttribute name="private" type="boolean" description="whether or not the ip belongs to a reversed network space" />
                </BasicAttribute>
                <BasicAttribute name="as" type="object" description="" :depth="3">
                  <BasicAttribute name="as_number" type="integer" description="The AS number of the organization owning the ip address allocation (nullable)" />
                  <BasicAttribute name="organization" type="string" description="The name of the organization owning the ip address allocation" />
                </BasicAttribute>
                <BasicAttribute name="company" type="object" description="" :depth="3">
                  <BasicAttribute name="name" type="string" description="The entity managing the ip address" />
                  <BasicAttribute name="business" type="string array" description="A list of services the company provides as part of its business" />
                  <BasicAttribute name="description" type="string" description="A brief description of who the network provider is" />
                </BasicAttribute>
                <BasicAttribute name="hostnames" type="string array" description="List of hostnames announced by the IP or operator" />
              </BasicAttribute>

              <BasicAttribute name="webscout_anonymization" type="object" description="Anonymization intelligence" :depth="2">
                <BasicAttribute name="residential" type="boolean" description="whether any associated service attributes this ip as a residential proxy" :depth="3" />
                <BasicAttribute name="proxy" type="boolean" description="whether any associated service attributes this ip as a proxy" :depth="3" />
                <BasicAttribute name="vpn" type="boolean" description="whether any associated service attributes this ip as a vpn" :depth="3" />
                <BasicAttribute name="services" type="array" description="List of anonymization service providers associated with this IP" :depth="3">
                  <BasicAttribute name="provider" type="string" description="The canonical provider identifier" />
                  <BasicAttribute name="display_name" type="string" description="Pretty display name of the provider" />
                  <BasicAttribute name="thumbnail_url" type="string" description="URL to provider logo/icon" />
                  <BasicAttribute name="type" type="string" description="Anonymization type for this service: residential, proxy, or vpn" />
                </BasicAttribute>
              </BasicAttribute>

              <BasicAttribute name="webscout_osint" type="object" description="OSINT reporting intelligence" :depth="2">
                <BasicAttribute name="services" type="array" description="List of OSINT providers that have reported on this IP" :depth="3">
                  <BasicAttribute name="provider" type="string" description="The canonical provider identifier" />
                  <BasicAttribute name="display_name" type="string" description="Pretty display name of the provider" />
                  <BasicAttribute name="thumbnail_url" type="string" description="URL to provider logo/icon" />
                  <BasicAttribute name="tags" type="string array" description="Activity tags reported by this provider" />
                  <BasicAttribute name="first_seen" type="string" description="When this provider first reported on this IP" />
                  <BasicAttribute name="last_seen" type="string" description="When this provider last reported on this IP" />
                </BasicAttribute>
                <BasicAttribute name="first_seen" type="string" description="Earliest reporting time across all providers" :depth="3" />
                <BasicAttribute name="last_seen" type="string" description="Most recent reporting time across all providers" :depth="3" />
              </BasicAttribute>

              <BasicAttribute name="webscout_observations" type="object" description="Event observation counts from the user's accessible data sources" :depth="2">
                <BasicAttribute name="total_count" type="integer" description="Total number of observations across all sources" :depth="3" />
                <BasicAttribute name="breakdown" type="array" description="Per-source or per-group observation counts" :depth="3">
                  <BasicAttribute name="name" type="string" description="Source or group identifier" />
                  <BasicAttribute name="display_name" type="string" description="Human-readable name" />
                  <BasicAttribute name="is_group" type="boolean" description="Whether this entry represents a source group" />
                  <BasicAttribute name="count" type="integer" description="Number of observations from this source or group" />
                </BasicAttribute>
              </BasicAttribute>

            </BasicAttribute>

          </BasicAttribute>


        </div>

      </template>

      <template v-slot:right>
        <endpoint-block
          :title="dynamic_title"
          method="GET"
          name="IP Information"
          :query="get_query"
        />

        <json-block title="Response">
{
"status": "success",
"data": {
"ip": "89.36.76.126",
"enrichments": {
"webscout_geo": {
"location": {
"country_iso": "DE",
"city": "Berlin"
},
"network": {
"type": "Cloud",
"service": "",
"region": "",
"range": "89.36.76.0/24",
"private": false
},
"as": {
"organization": "M247 Europe SRL",
"as_number": 9009
},
"company": {
"name": "M247",
"business": [
"Backup/Recovery",
"Business Internet",
"Cloud",
"Colocation",
"Hosting",
"VPC"
],
"description": "An internet service provider offering a range of services including broadband connectivity, data center solutions, and cloud services."
},
"hostnames": null
},
"webscout_anonymization": {
"residential": false,
"proxy": false,
"vpn": true,
"services": [
{
"provider": "surfshark",
"display_name": "Surfshark VPN",
"thumbnail_url": "https://example.com/surfshark-logo.png",
"type": "vpn"
}
]
},
"webscout_osint": {
"services": [
{
"provider": "abuseipdb",
"display_name": "AbuseIPDB",
"thumbnail_url": "https://example.com/abuseipdb-logo.png",
"tags": [
"malware",
"bruteforce"
],
"first_seen": "2024-01-15T10:30:00Z",
"last_seen": "2024-03-20T15:45:00Z"
}
],
"first_seen": "2024-01-15T10:30:00Z",
"last_seen": "2024-03-20T15:45:00Z"
},
"webscout_observations": {
"total_count": 1234,
"breakdown": [
{
"name": "network-sensors",
"display_name": "Network Sensors",
"is_group": true,
"count": 1100
},
{
"name": "honeypot-1",
"display_name": "Honeypot 1",
"is_group": false,
"count": 134
}
]
}
}
}
}
        </json-block>
      </template>
    </doc-section>
  </div>
</template>

<style scoped>
.btn-expand-collapse {
  background-color: transparent;
  border: 1px solid var(--border-subtle);
  color: var(--text-faint);
  padding: 0.5rem 1rem;
  border-radius: var(--radius-md);
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-expand-collapse:hover {
  background-color: var(--surface-overlay-2);
  color: var(--text-primary);
}
</style>
